import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Calendar, DollarSign, Clock, Cake, PlayCircle, FileText, ShoppingBag, Glasses, Eye, Package, Hash } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// ─── Animations ─────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Overlay / Shell ────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #ffffff;
  border-radius: 18px;
  width: 100%;
  max-width: 920px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 32px 64px -12px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.05);
  overflow: hidden;
  animation: ${fadeSlideUp} 0.22s ease both;
`;

// ─── Header ─────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  ['#6366f1','#eef2ff'],
  ['#8b5cf6','#fdf4ff'],
  ['#0d9488','#f0fdfa'],
  ['#ea580c','#fff7ed'],
  ['#2563eb','#eff6ff'],
  ['#dc2626','#fef2f2'],
  ['#16a34a','#f0fdf4'],
];

function getAvatarPalette(name = '') {
  const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function getInitials(first, last) {
  return [(first || '').charAt(0), (last || '').charAt(0)]
    .filter(Boolean).join('').toUpperCase();
}

const ModalHeader = styled.div`
  padding: 22px 24px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: #fff;
`;

const ClientLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const HeaderAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${props => props.bg};
  color: ${props => props.fg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  flex-shrink: 0;
  letter-spacing: 0.5px;
`;

const ClientMeta = styled.div`
  min-width: 0;

  h2 {
    margin: 0 0 5px;
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pills {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e8edf3;
  padding: 3px 10px;
  border-radius: 999px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const StartVisitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
  box-shadow: 0 2px 8px rgba(99,102,241,0.3);
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99,102,241,0.35);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(99,102,241,0.3);
  }
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
  }
`;

// ─── Tabs ─────────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 10px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbff;
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  background: ${props => props.$active ? '#fff' : 'transparent'};
  color:      ${props => props.$active ? '#4f46e5' : '#64748b'};
  box-shadow: ${props => props.$active ? '0 1px 4px rgba(0,0,0,0.08), 0 0 0 1px #e0e7ff' : 'none'};

  span.badge {
    font-size: 11px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 999px;
    background: ${props => props.$active ? '#eef2ff' : '#f1f5f9'};
    color:      ${props => props.$active ? '#4f46e5' : '#94a3b8'};
  }

  &:hover:not([data-active]) {
    background: #f1f5f9;
    color: #334155;
  }
`;

// ─── Body ──────────────────────────────────────────────────────────────────────────

const ModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 24px;
  background: #fff;
`;

const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// ─── Prescription table ─────────────────────────────────────────────────────────

const RxCard = styled.div`
  border: 1.5px solid #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #e0e7ff;
    box-shadow: 0 4px 16px rgba(99,102,241,0.07);
  }
`;

const RxCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafbff;
  border-bottom: 1px solid #f1f5f9;
  gap: 12px;
  flex-wrap: wrap;
`;

const RxNum = styled.strong`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const RxMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const RxMetaItem = styled.span`
  font-size: 12.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CorrectionTag = styled.span`
  padding: 2px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 11.5px;
  font-weight: 700;
`;

const RxCardBody = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CorrectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const EyeTable = styled.table`
  width: 100%;
  min-width: 380px;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 6px 10px;
    background: #f8fafc;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #f1f5f9;
  }

  td {
    padding: 8px 10px;
    color: #0f172a;
    font-weight: 500;
    border-bottom: 1px solid #f8fafc;
  }

  tr:last-child td { border-bottom: none; }

  td:first-child { color: #475569; font-weight: 600; }
`;

const PhotoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  color: #475569;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  width: fit-content;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #1e293b;
  }
`;

// ─── Order / Visit cards ────────────────────────────────────────────────────────

const Card = styled.div`
  border: 1.5px solid #f1f5f9;
  border-radius: 12px;
  padding: 16px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #e0e7ff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.05);
  }
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const CardDateBadge = styled.span`
  font-size: 12px;
  color: #94a3b8;
  background: #f8fafc;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #f1f5f9;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  ${props => {
    switch (props.$status) {
      case 'completed': case 'delivered': return 'background:#dcfce7; color:#15803d;';
      case 'cancelled':                   return 'background:#fee2e2; color:#b91c1c;';
      case 'pending':                     return 'background:#fef9c3; color:#854d0e;';
      default:                            return 'background:#f1f5f9; color:#475569;';
    }
  }}
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const InfoItem = styled.div`
  label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    margin-bottom: 4px;
  }
  p {
    margin: 0;
    font-size: 13.5px;
    font-weight: 600;
    color: #0f172a;
  }
`;

const FrameNote = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 12.5px;
  color: #475569;
`;

// ─── Item list (inside Order card) ───────────────────────────────────────────────

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  border-top: 1px dashed #e8edf3;
  padding-top: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  background: #fafbff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
`;

const ItemIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.bg || '#eef2ff'};
  color: ${props => props.fg || '#4f46e5'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemMain = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    font-size: 11.5px;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

const ItemPrice = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  text-align: right;

  .qty {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

const PriceBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
  font-size: 12.5px;

  .row {
    display: flex;
    justify-content: space-between;
    color: #64748b;
  }
  .row.total {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px solid #f1f5f9;
    font-weight: 700;
    color: #0f172a;
    font-size: 13.5px;
  }
`;

const NotesNote = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12px;
  color: #78350f;
  font-style: italic;
`;

// ─── Skeleton ────────────────────────────────────────────────────────────────────

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf3 50%, #f1f5f9 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 8px;
`;

const SkeletonBlock = ({ h = 16, w = '100%', style }) => (
  <SkeletonBase style={{ height: h, width: w, ...style }} />
);

const LoadingSkeleton = () => (
  <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
    {[120, 90, 150, 90].map((w, i) => <SkeletonBlock key={i} h={18} w={`${w}px`} />)}
  </div>
);

// ─── Empty state ─────────────────────────────────────────────────────────────────

const EmptyMsg = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;

  svg { margin-bottom: 10px; opacity: 0.35; }

  p { margin: 0; font-size: 13.5px; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────────

function formatRxValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function formatDate(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return `${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function formatPrescriptionType(type) {
  const labels = { single_vision: 'Single vision', progressive: 'Progressive' };
  return labels[type] || (type || 'N/A').replace(/_/g, ' ');
}

function getCorrectionLabel(rx) {
  if (rx.prescriptionType === 'single_vision') {
    if (rx.singleVisionCorrectionType === 'closeup') return 'Close-up';
    if (rx.singleVisionCorrectionType === 'far') return 'Far';
    return 'Single vision';
  }
  if (rx.prescriptionType === 'progressive') return 'Far';
  return 'Correction';
}

// Collapse duplicate prescriptions (identical clinical content) into one,
// keeping the copy that has a prescription photo. The same script sometimes
// gets saved twice — once with the photo, once without.
function dedupePrescriptions(list) {
  if (!Array.isArray(list)) return [];
  const EYE_FIELDS = [
    'rightEye', 'leftEye',
    'rightEyeFar', 'leftEyeFar',
    'rightEyeCloseUp', 'leftEyeCloseUp',
    'progressiveRightEyeFar', 'progressiveLeftEyeFar',
    'progressiveRightEyeCloseUp', 'progressiveLeftEyeCloseUp',
  ];
  const sigOf = (rx) => {
    const eyes = EYE_FIELDS.map((f) => JSON.stringify(rx?.[f] ?? null)).join('|');
    const date = String(rx?.prescriptionDate || rx?.createdAt || '').slice(0, 10);
    return [
      rx?.prescriptionType || '',
      rx?.singleVisionCorrectionType || rx?.single_vision_correction_type || '',
      String(rx?.prescribedBy || '').trim().toLowerCase(),
      date,
      eyes,
    ].join('::');
  };
  const map = new Map();
  for (const rx of list) {
    const sig = sigOf(rx);
    const existing = map.get(sig);
    if (!existing) { map.set(sig, rx); continue; }
    // Prefer the copy that has a photo; otherwise keep the first one seen.
    if (!existing.prescriptionPhoto && rx?.prescriptionPhoto) map.set(sig, rx);
  }
  return Array.from(map.values());
}

function hasEyeData(eye) {
  if (!eye || typeof eye !== 'object') return false;
  return ['sphere', 'cylinder', 'axis', 'add', 'pd', 'prism'].some(
    k => eye[k] !== null && eye[k] !== undefined && eye[k] !== ''
  );
}

function getPrescriptionEyeBlocks(rx) {
  const blocks = [];
  const type = rx.prescriptionType;

  if (type === 'progressive') {
    if (!rx.progressiveFarNotAvailable && (hasEyeData(rx.progressiveRightEyeFar) || hasEyeData(rx.progressiveLeftEyeFar)))
      blocks.push({ title: 'Far vision', od: rx.progressiveRightEyeFar, os: rx.progressiveLeftEyeFar });
    if (!blocks.length && (hasEyeData(rx.rightEye) || hasEyeData(rx.leftEye)))
      blocks.push({ title: 'Correction', od: rx.rightEye, os: rx.leftEye });
    if (!blocks.length)
      blocks.push({ title: 'Progressive', od: rx.progressiveRightEyeFar || rx.rightEye, os: rx.progressiveLeftEyeFar || rx.leftEye });
    return blocks;
  }

  if (type === 'single_vision') {
    const corr = rx.singleVisionCorrectionType;
    if (corr === 'far') {
      blocks.push({ title: 'Far vision', od: rx.rightEyeFar || rx.rightEye, os: rx.leftEyeFar || rx.leftEye });
    } else if (corr === 'closeup') {
      blocks.push({ title: 'Close-up', od: rx.rightEyeCloseUp || rx.rightEye, os: rx.leftEyeCloseUp || rx.leftEye });
    } else {
      if (hasEyeData(rx.rightEyeFar) || hasEyeData(rx.leftEyeFar))
        blocks.push({ title: 'Far', od: rx.rightEyeFar, os: rx.leftEyeFar });
      if (hasEyeData(rx.rightEyeCloseUp) || hasEyeData(rx.leftEyeCloseUp))
        blocks.push({ title: 'Close-up', od: rx.rightEyeCloseUp, os: rx.leftEyeCloseUp });
      if (!blocks.length)
        blocks.push({ title: 'Single vision', od: rx.rightEye, os: rx.leftEye });
    }
    return blocks;
  }

  const fallback = [];
  if (hasEyeData(rx.rightEye) || hasEyeData(rx.leftEye))
    fallback.push({ title: 'Standard', od: rx.rightEye, os: rx.leftEye });
  if (hasEyeData(rx.rightEyeFar) || hasEyeData(rx.leftEyeFar))
    fallback.push({ title: 'Far', od: rx.rightEyeFar, os: rx.leftEyeFar });
  if (hasEyeData(rx.rightEyeCloseUp) || hasEyeData(rx.leftEyeCloseUp))
    fallback.push({ title: 'Close-up', od: rx.rightEyeCloseUp, os: rx.leftEyeCloseUp });
  if (hasEyeData(rx.progressiveRightEyeFar) || hasEyeData(rx.progressiveLeftEyeFar))
    fallback.push({ title: 'Progressive — far', od: rx.progressiveRightEyeFar, os: rx.progressiveLeftEyeFar });
  if (hasEyeData(rx.progressiveRightEyeCloseUp) || hasEyeData(rx.progressiveLeftEyeCloseUp))
    fallback.push({ title: 'Progressive — close-up', od: rx.progressiveRightEyeCloseUp, os: rx.progressiveLeftEyeCloseUp });
  return fallback.length ? fallback : [{ title: 'Correction', od: rx.rightEye, os: rx.leftEye }];
}

function EyeBlocks({ rx }) {
  const { t } = useTranslation();
  const blocks = getPrescriptionEyeBlocks(rx);
  const showAddAndPd = rx.prescriptionType !== 'progressive';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {blocks.map((block, idx) => {
        const od = block.od || {};
        const os = block.os || {};
        return (
          <div key={`${rx._id}-${block.title}-${idx}`} style={{ overflowX: 'auto' }}>
            <CorrectionTitle>{block.title}</CorrectionTitle>
            <EyeTable>
              <thead>
                <tr>
                  <th>{t('clientDetails.eye')}</th><th>{t('clientDetails.sph')}</th><th>{t('clientDetails.cyl')}</th><th>{t('clientDetails.axis')}</th>{showAddAndPd && <><th>{t('clientDetails.add')}</th><th>{t('clientDetails.pd')}</th></>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('clientDetails.rightOd')}</td>
                  <td>{formatRxValue(od.sphere)}</td>
                  <td>{formatRxValue(od.cylinder)}</td>
                  <td>{formatRxValue(od.axis)}</td>
                  {showAddAndPd && <><td>{formatRxValue(od.add)}</td><td>{formatRxValue(od.pd || rx.pupillaryDistance)}</td></>}
                </tr>
                <tr>
                  <td>{t('clientDetails.leftOs')}</td>
                  <td>{formatRxValue(os.sphere)}</td>
                  <td>{formatRxValue(os.cylinder)}</td>
                  <td>{formatRxValue(os.axis)}</td>
                  {showAddAndPd && <><td>{formatRxValue(os.add)}</td><td>{formatRxValue(os.pd || rx.pupillaryDistance)}</td></>}
                </tr>
              </tbody>
            </EyeTable>
          </div>
        );
      })}
    </div>
  );
}

// ─── Order helpers ───────────────────────────────────────────────────────────────

const ITEM_TYPE_STYLE = {
  frame:      { bg: '#eef2ff', fg: '#4f46e5', Icon: Glasses, label: 'Frame' },
  lens:       { bg: '#f0fdfa', fg: '#0d9488', Icon: Eye,     label: 'Lens' },
  lenses:     { bg: '#f0fdfa', fg: '#0d9488', Icon: Eye,     label: 'Lens' },
  accessory:  { bg: '#fff7ed', fg: '#ea580c', Icon: Package, label: 'Accessory' },
};

function getItemStyle(type) {
  const key = (type || '').toLowerCase();
  return ITEM_TYPE_STYLE[key] || { bg: '#f1f5f9', fg: '#64748b', Icon: Package, label: type || 'Item' };
}

function buildItemSubtitle(item, order) {
  const parts = [];
  if (item.brand) parts.push(item.brand);
  if (item.model) parts.push(item.model);
  if (item.color) parts.push(item.color);
  if (item.material) parts.push(item.material);
  if (item.size) parts.push(item.size);

  // Fall back to buyerChoice/lensChoice when order_items has no detail
  if (parts.length === 0) {
    const key = (item.type || '').toLowerCase();
    if (key === 'frame' && order.buyerChoice) {
      if (order.buyerChoice.frameType) parts.push(order.buyerChoice.frameType);
      if (order.buyerChoice.frameColor) parts.push(order.buyerChoice.frameColor);
      if (order.buyerChoice.frameBrand) parts.push(order.buyerChoice.frameBrand);
    } else if ((key === 'lens' || key === 'lenses') && order.lensChoice) {
      if (order.lensChoice.lensType) parts.push(order.lensChoice.lensType);
      if (order.lensChoice.material) parts.push(order.lensChoice.material);
      if (order.lensChoice.index) parts.push(`Index ${order.lensChoice.index}`);
      if (order.lensChoice.coating) parts.push(order.lensChoice.coating);
    }
  }
  return parts.filter(Boolean).join(' · ');
}

function formatPrice(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

// Synthesize line items from buyerChoice/lensChoice when order_items is empty
function getDisplayItems(order) {
  if (order.items && order.items.length) return order.items;
  const synthetic = [];
  if (order.buyerChoice) {
    synthetic.push({
      _id: `${order._id}-frame`,
      type: 'frame',
      name: order.buyerChoice.frameType || 'Frame',
      brand: order.buyerChoice.frameBrand,
      color: order.buyerChoice.frameColor,
      quantity: 1,
      totalPrice: order.buyerChoice.framePrice,
    });
  }
  if (order.lensChoice) {
    synthetic.push({
      _id: `${order._id}-lens`,
      type: 'lens',
      name: order.lensChoice.lensType || 'Lens',
      material: order.lensChoice.material,
      quantity: 1,
      totalPrice: order.lensChoice.lensPrice,
    });
  }
  return synthetic;
}

function OrderCard({ order }) {
  const { t } = useTranslation();
  const items = getDisplayItems(order);
  const hasPaidFrame = items.some(it =>
    (it.type || '').toLowerCase() === 'frame' && Number(it.totalPrice) > 0
  );
  const showBreakdown = hasPaidFrame && (
    (order.tax != null && Number(order.tax) > 0) ||
    (order.discount != null && Number(order.discount) > 0)
  );

  return (
    <Card>
      <CardRow>
        <CardTitle>
          <Hash size={14} color="#6366f1" />
          {order.orderNumber ? `Order ${order.orderNumber}` : t('clientDetails.order')}
        </CardTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StatusBadge $status={(order.status || '').toLowerCase()}>
            {(order.status || 'pending').replace(/_/g, ' ')}
          </StatusBadge>
          <CardDateBadge>{new Date(order.createdAt).toLocaleDateString()}</CardDateBadge>
        </div>
      </CardRow>

      {items.length > 0 ? (
        <ItemList>
          {items.map(item => {
            const style = getItemStyle(item.type);
            const Icon = style.Icon;
            const subtitle = buildItemSubtitle(item, order);
            return (
              <ItemRow key={item._id}>
                <ItemIcon bg={style.bg} fg={style.fg}>
                  <Icon size={16} />
                </ItemIcon>
                <ItemMain>
                  <div className="name">{item.name || style.label}</div>
                  {subtitle && <div className="sub">{subtitle}</div>}
                </ItemMain>
                <ItemPrice>
                  {formatPrice(item.totalPrice)} {t('clientDetails.dzd')}
                  {(item.quantity || 1) > 1 && <span className="qty">× {item.quantity}</span>}
                </ItemPrice>
              </ItemRow>
            );
          })}
        </ItemList>
      ) : (
        <FrameNote>{t('clientDetails.noItemDetailsOnFile')}</FrameNote>
      )}

      {showBreakdown ? (
        <PriceBreakdown>
          {order.subtotal != null && (
            <div className="row"><span>{t('clientDetails.subtotal')}</span><span>{formatPrice(order.subtotal)} {t('clientDetails.dzd')}</span></div>
          )}
          {order.discount != null && Number(order.discount) > 0 && (
            <div className="row"><span>{t('clientDetails.discount')}</span><span>−{formatPrice(order.discount)} {t('clientDetails.dzd')}</span></div>
          )}
          {order.tax != null && Number(order.tax) > 0 && (
            <div className="row"><span>{t('clientDetails.tax')}</span><span>{formatPrice(order.tax)} {t('clientDetails.dzd')}</span></div>
          )}
          <div className="row total"><span>{t('clientDetails.total')}</span><span>{formatPrice(order.total)} {t('clientDetails.dzd')}</span></div>
        </PriceBreakdown>
      ) : (
        order.total != null && (
          <PriceBreakdown>
            <div className="row total"><span>{t('clientDetails.total')}</span><span>{formatPrice(order.total)} {t('clientDetails.dzd')}</span></div>
          </PriceBreakdown>
        )
      )}

      {order.notes && <NotesNote>{order.notes}</NotesNote>}
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────────

const ClientDetailsModal = ({ clientIdentifier, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('prescriptions');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  useEffect(() => {
    if (!clientIdentifier) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `/api/visitors/client-details/${encodeURIComponent(String(clientIdentifier))}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setData(res.data);
      } catch (err) {
        console.error('Error fetching client details:', err);
        setError('Failed to load client history');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientIdentifier]);

  if (!clientIdentifier) return null;

  const lastVisitDate = data?.profile?.visitDate
    || data?.visitors?.[0]?.visitDate
    || data?.prescriptions?.[0]?.prescriptionDate;

  const handleStartVisit = () => {
    if (!data?.profile) return;
    const p = data.profile;
    const previousPrescription = dedupePrescriptions(data.prescriptions || [])[0] || null;
    const visitorData = {
      gender: p.gender || '',
      ageGroup: p.ageGroup || '',
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      phone: p.phone || '',
      email: p.email || '',
      address: p.address || '',
      patientBirthday: p.patientBirthday || '',
      patientAge: p.patientAge || '',
      previousClientId: p._id || p.id || clientIdentifier,
    };
    sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
    sessionStorage.setItem('returningClientData', JSON.stringify(visitorData));
    if (previousPrescription) {
      sessionStorage.setItem('previousPrescription', JSON.stringify({
        clientId: visitorData.previousClientId,
        prescription: previousPrescription
      }));
    } else {
      sessionStorage.removeItem('previousPrescription');
    }
    sessionStorage.removeItem('visitorId');
    // Seed the Visit step so it stays consistent if the sales person jumps
    // back to it via the progress bar (personal info is prefilled on RX below).
    sessionStorage.setItem('visitorFormData', JSON.stringify({
      gender: p.gender || '',
      ageGroup: p.ageGroup || ''
    }));
    sessionStorage.removeItem('prescriptionId');
    sessionStorage.removeItem('existingPrescription');
    sessionStorage.removeItem('prescriptionFormData');
    // A new visit starts fresh: drop the previous visit's captured photo and
    // order selection so they aren't restored into this new prescription.
    sessionStorage.removeItem('prescriptionPhotoState');
    sessionStorage.removeItem('hasPrescriptionPhoto');
    sessionStorage.removeItem('prescriptionOrderState');
    onClose();
    navigate('/prescription');
  };

  const clientName = data
    ? `${data.profile?.firstName || ''} ${data.profile?.lastName || ''}`.trim()
    : '';
  const [avatarFg, avatarBg] = getAvatarPalette(clientName);
  const initials = data ? getInitials(data.profile?.firstName, data.profile?.lastName) : '';

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>

        {/* ── Loading state ── */}
        {loading && (
          <>
            <ModalHeader>
              <ClientLeft>
                <SkeletonBase style={{ width: 52, height: 52, borderRadius: 14 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SkeletonBase style={{ width: 180, height: 18 }} />
                  <SkeletonBase style={{ width: 240, height: 14 }} />
                </div>
              </ClientLeft>
            </ModalHeader>
            <LoadingSkeleton />
          </>
        )}

        {/* ── Error state ── */}
        {!loading && error && (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: 12 }}>{error}</p>
            <button
              onClick={onClose}
              style={{ padding: '8px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}
            >
              {t('clientDetails.close')}
            </button>
          </div>
        )}

        {/* ── Content ── */}
        {!loading && !error && data && (
          <>
            {/* Header */}
            <ModalHeader>
              <ClientLeft>
                <HeaderAvatar bg={avatarBg} fg={avatarFg}>{initials || '?'}</HeaderAvatar>
                <ClientMeta>
                  <h2>{clientName || 'Unknown Client'}</h2>
                  <div className="pills">
                    <MetaPill>
                      <Cake size={12} /> {formatDate(data.profile.patientBirthday)}
                    </MetaPill>
                    <MetaPill>
                      <Clock size={12} /> {t('clientDetails.lastVisit')} {formatDate(lastVisitDate)}
                    </MetaPill>
                  </div>
                </ClientMeta>
              </ClientLeft>

              <HeaderActions>
                <StartVisitBtn type="button" onClick={handleStartVisit}>
                  <PlayCircle size={15} /> {t('clientDetails.startNewVisit')}
                </StartVisitBtn>
                <CloseBtn onClick={onClose} aria-label={t('clientDetails.close')}>
                  <X size={18} />
                </CloseBtn>
              </HeaderActions>
            </ModalHeader>

            {/* Tabs */}
            <TabBar>
              <Tab $active={activeTab === 'prescriptions'} onClick={() => setActiveTab('prescriptions')}>
                <FileText size={14} /> {t('clientDetails.prescriptions')}
                <span className="badge">{dedupePrescriptions(data.prescriptions).length}</span>
              </Tab>
              <Tab $active={activeTab === 'orders'} onClick={() => setActiveTab('orders')}>
                <ShoppingBag size={14} /> {t('clientDetails.orders')}
                <span className="badge">{data.orders.length}</span>
              </Tab>
              <Tab $active={activeTab === 'visits'} onClick={() => setActiveTab('visits')}>
                <Calendar size={14} /> {t('clientDetails.visits')}
                <span className="badge">{data.visitors.length}</span>
              </Tab>
            </TabBar>

            {/* Body */}
            <ModalBody>

              {/* ── Prescriptions ── */}
              {activeTab === 'prescriptions' && (() => {
                const prescriptions = dedupePrescriptions(data.prescriptions);
                return prescriptions.length === 0 ? (
                  <EmptyMsg>
                    <FileText size={36} />
                    <p>{t('clientDetails.noPrescriptionsOnRecord')}</p>
                  </EmptyMsg>
                ) : (
                  <TabPanel>
                  {prescriptions.map((rx, idx) => (
                    <RxCard key={rx._id || idx}>
                      <RxCardHead>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <RxNum>#{prescriptions.length - idx}</RxNum>
                          <CorrectionTag>{getCorrectionLabel(rx)}</CorrectionTag>
                          <span style={{ fontSize: 12.5, color: '#94a3b8' }}>{formatPrescriptionType(rx.prescriptionType)}</span>
                        </div>
                        <RxMeta>
                          {rx.prescribedBy && (
                            <RxMetaItem>{t('clientDetails.dr')} {rx.prescribedBy.replace(/^Dr\.?\s*/i, '')}</RxMetaItem>
                          )}
                          <RxMetaItem>
                            <Calendar size={12} /> {formatDate(rx.prescriptionDate || rx.createdAt)}
                          </RxMetaItem>
                          {rx.prescriptionPhoto && (
                            <PhotoBtn onClick={() => setViewingPhoto(rx.prescriptionPhoto)}>
                              {t('clientDetails.viewPhoto')}
                            </PhotoBtn>
                          )}
                        </RxMeta>
                      </RxCardHead>
                      <RxCardBody>
                        <EyeBlocks rx={rx} />
                      </RxCardBody>
                    </RxCard>
                  ))}
                  </TabPanel>
                );
              })()}

              {/* ── Orders ── */}
              {activeTab === 'orders' && (
                data.orders.length === 0 ? (
                  <EmptyMsg>
                    <ShoppingBag size={36} />
                    <p>{t('clientDetails.noOrdersOnRecord')}</p>
                  </EmptyMsg>
                ) : (
                  <TabPanel>
                  {data.orders.map(order => <OrderCard key={order._id} order={order} />)}
                  </TabPanel>
                )
              )}

              {/* ── Visits ── */}
              {activeTab === 'visits' && (
                data.visitors.length === 0 ? (
                  <EmptyMsg>
                    <Calendar size={36} />
                    <p>{t('clientDetails.noVisitsOnRecord')}</p>
                  </EmptyMsg>
                ) : (
                  <TabPanel>
                  {data.visitors.map(visit => (
                    <Card key={visit._id}>
                      <CardRow>
                        <CardTitle>
                          <Calendar size={15} color="#6366f1" /> {t('clientDetails.visit')}
                        </CardTitle>
                        <CardDateBadge>{formatDateTime(visit.visitDate)}</CardDateBadge>
                      </CardRow>
                      <InfoGrid>
                        <InfoItem>
                          <label>{t('clientDetails.servedBy')}</label>
                          <p>{visit.salesPerson
                            ? `${visit.salesPerson.firstName || ''} ${visit.salesPerson.lastName || ''}`.trim() || '—'
                            : '—'}
                          </p>
                        </InfoItem>
                        <InfoItem>
                          <label>{t('clientDetails.motive')}</label>
                          <p>{visit.searchMotive || '—'}</p>
                        </InfoItem>
                      </InfoGrid>
                    </Card>
                  ))}
                  </TabPanel>
                )
              )}

            </ModalBody>
          </>
        )}
      </Modal>

      {/* ── Photo lightbox ── */}
      {viewingPhoto && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
          onClick={() => setViewingPhoto(null)}
        >
          <img
            src={viewingPhoto}
            alt={t('clientDetails.prescription')}
            style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 8 }}
          />
          <button
            style={{
              position: 'absolute', top: 20, right: 20,
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.2)',
              color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setViewingPhoto(null)}
          >
            <X size={20} />
          </button>
        </div>
      )}
    </Overlay>
  );
};

export default ClientDetailsModal;
